import { Controller } from "@hotwired/stimulus"

// Tabs controller with state-based styling
// Tab state classes defined in: app/assets/stylesheets/application.tailwind.css
// Available states are passed from Ruby via data-tabs-states-value
//
// Usage:
//   <div data-controller="tabs" 
//        data-tabs-states-value='["active", "disabled"]'
//        data-action="tabs:configure@document->tabs#configureTabs">
//     <button class="tab" data-action="tabs#switch" data-tabs-target="tab" data-tab="front">Front</button>
//     <button class="tab" data-action="tabs#switch" data-tabs-target="tab" data-tab="back">Back</button>
//     
//     <div data-tabs-target="panel" data-tab="front">Front content</div>
//     <div data-tabs-target="panel" data-tab="back" class="hidden">Back content</div>
//   </div>
//
// To update tab states, dispatch a custom event:
//   document.dispatchEvent(new CustomEvent("tabs:set-states", {
//     detail: { front: ['active'], back: ['disabled'] }
//   }))
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = { 
    id: String,
    states: Array,
    initialState: Object,
  }

  connect() {
    // Apply initial state configuration from Ruby
    if (this.hasInitialStateValue) {
      this.setStates({ detail: this.initialStateValue })
    }
  }

  // Handle user clicking a tab
  switch(event) {
    const tab = event.currentTarget
    if (tab.disabled) {
      return
    }
    
    this.activateTab(tab.dataset.tab)
  }

  // Set tab states from external event
  // config format: { panelId: 'panel-id', tabName: ['state1', 'state2'], ... }
  setStates(event) {
    const config = event.detail || {}
    
    // Filter by panel ID if provided - allows multiple panels on same page
    if (config.panelId && config.panelId !== this.idValue) {
      return
    }
    
    let activeTab = null
    
    this.tabTargets.forEach(tab => {
      const tabName = tab.dataset.tab
      const states = config[tabName] || []
      
      // Track which tab should be active
      if (states.includes("active")) {
        activeTab = tabName
      }
      
      // Apply each known state (from Ruby-provided statesValue)
      this.statesValue.forEach(stateName => {
        this.toggleState(tab, stateName, states.includes(stateName))
      })
    })
    
    // Show the panel for the active tab
    if (activeTab) {
      this.showPanel(activeTab)
    }
  }

  // Toggle a specific state on a tab
  toggleState(tab, stateName, isOn) {
    tab.classList.toggle(`tab-${stateName}-on`, isOn)
    tab.classList.toggle(`tab-${stateName}-off`, !isOn)
    
    // Special case: disabled state also needs the HTML disabled attribute
    // to prevent clicks and provide semantic meaning
    if (stateName === "disabled") {
      tab.disabled = isOn
    }
  }

  // Activate a specific tab by name
  activateTab(tabName) {
    if (!tabName) {
      return
    }
    
    this.tabTargets.forEach(tab => {
      this.toggleState(tab, "active", tab.dataset.tab === tabName)
    })
    
    this.showPanel(tabName)
  }

  // Show the panel for the active tab
  showPanel(tabName) {
    this.panelTargets.forEach(panel => {
      panel.classList.toggle("hidden", panel.dataset.tab !== tabName)
    })
  }
}

// Export for use as Turbo Stream action, to be registered in turbo_stream_actions/index.js
export function setTabStates() {
  const config = JSON.parse(this.target)
  document.dispatchEvent(new CustomEvent("tabs:set-states", { 
    detail: config,
    bubbles: true,
  }))
}
